import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Shield, RotateCcw, Lock } from "lucide-react";
import { toast } from "sonner";
import { TIER_LABEL, TIER_STYLE } from "@/lib/constants";

const CHIP = {
  read: { letter: "V", on: "bg-blue-100 text-blue-700 border-blue-300", label: "View" },
  create: { letter: "A", on: "bg-emerald-100 text-emerald-700 border-emerald-300", label: "Add" },
  update: { letter: "E", on: "bg-amber-100 text-amber-700 border-amber-300", label: "Edit" },
  delete: { letter: "D", on: "bg-rose-100 text-rose-700 border-rose-300", label: "Delete" },
};

function cellActive(cell, key) {
  if (!cell) return false;
  const v = cell[key];
  return v === true || (Array.isArray(v) && v.length > 0);
}

export default function AccessMatrixPage() {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupFilter, setGroupFilter] = useState("all");
  const [editing, setEditing] = useState(null); // {role, module_key, draft}
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/access/matrix")
      .then((r) => setMatrix(r.data))
      .catch(() => toast.error("Failed to load access matrix"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const groups = useMemo(
    () => (matrix ? [...new Set(matrix.modules.map((m) => m.group))] : []),
    [matrix]
  );
  const visibleModules = useMemo(
    () => (matrix ? matrix.modules.filter((m) => groupFilter === "all" || m.group === groupFilter) : []),
    [matrix, groupFilter]
  );

  const openEditor = (role, mk) => {
    const cell = matrix.cells[role][mk];
    if (cell.source === "exec") return; // exec roles are locked
    setEditing({
      role,
      module_key: mk,
      source: cell.source,
      partialUpdate: Array.isArray(cell.update) ? cell.update : null,
      draft: {
        read: !!cell.read,
        create: !!cell.create,
        update: cell.update === true,
        delete: !!cell.delete,
      },
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/access/matrix", {
        role: editing.role,
        module_key: editing.module_key,
        ...editing.draft,
      });
      toast.success("Permission updated — applies immediately");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const revert = async () => {
    setSaving(true);
    try {
      await api.delete(`/access/matrix?role=${editing.role}&module_key=${editing.module_key}`);
      toast.success("Reverted to default role permissions");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Revert failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !matrix) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4" data-testid="access-matrix-loading">
        <Skeleton className="h-10 w-96" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const roleLabel = (k) => matrix.roles.find((r) => r.key === k)?.label || k;
  const moduleLabel = (k) => matrix.modules.find((m) => m.key === k)?.label || k;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="access-matrix-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Admin</div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">Access Matrix</h1>
          <p className="text-sm text-slate-500 mt-1">
            Live permission grid — <span className="font-semibold">V</span>iew / <span className="font-semibold">A</span>dd / <span className="font-semibold">E</span>dit / <span className="font-semibold">D</span>elete per role per module.
            Click any cell to change it. Changes apply instantly.
          </p>
        </div>
        <Select value={groupFilter} onValueChange={setGroupFilter}>
          <SelectTrigger className="w-56" data-testid="matrix-group-filter">
            <SelectValue placeholder="All module groups" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All module groups</SelectItem>
            {groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm bg-amber-200 border border-amber-400" /> amber ring = admin override (differs from default)</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm bg-slate-200 border border-slate-300" /> grey = no access</span>
        <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> exec rows are locked</span>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="access-matrix-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left py-3 px-4 font-semibold text-slate-700 sticky left-0 bg-slate-50 z-10 min-w-[220px]">
                  Role
                </th>
                {visibleModules.map((m) => (
                  <th key={m.key} className="py-3 px-2 text-center align-bottom min-w-[92px]">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{m.group}</div>
                    <div className="text-xs font-semibold text-slate-700 leading-tight mt-0.5">{m.label}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.roles.map((r) => (
                <tr key={r.key} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`matrix-row-${r.key}`}>
                  <td className="py-2.5 px-4 sticky left-0 bg-white z-10">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800 text-sm">{r.label}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${TIER_STYLE[r.tier] || TIER_STYLE[3]}`}>
                        L{r.tier}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-400">{r.users} user{r.users === 1 ? "" : "s"}</div>
                  </td>
                  {visibleModules.map((m) => {
                    const cell = matrix.cells[r.key][m.key];
                    const locked = cell.source === "exec";
                    return (
                      <td key={m.key} className="py-2.5 px-2 text-center">
                        <button
                          type="button"
                          disabled={locked}
                          onClick={() => openEditor(r.key, m.key)}
                          title={locked ? "Executive roles always have full access" : `${roleLabel(r.key)} × ${moduleLabel(m.key)} — click to edit`}
                          data-testid={`cell-${r.key}-${m.key}`}
                          className={`inline-flex items-center gap-1 px-1.5 py-1 rounded-md border transition-colors ${
                            cell.source === "override" ? "border-amber-400 ring-1 ring-amber-200" : "border-transparent"
                          } ${locked ? "cursor-not-allowed opacity-80" : "hover:border-slate-300 cursor-pointer"}`}
                        >
                          {Object.entries(CHIP).map(([key, cfg]) => {
                            const active = cellActive(cell, key);
                            const partial = key === "update" && Array.isArray(cell.update);
                            return (
                              <span
                                key={key}
                                className={`inline-flex items-center justify-center h-5 w-5 rounded text-[10px] font-bold border ${
                                  active ? cfg.on : "bg-slate-100 text-slate-300 border-slate-200"
                                }`}
                              >
                                {cfg.letter}{partial && active ? "*" : ""}
                              </span>
                            );
                          })}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Cell editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="matrix-cell-editor">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-slate-500" />
                  {roleLabel(editing.role)} × {moduleLabel(editing.module_key)}
                </DialogTitle>
                <DialogDescription>
                  Toggle what this role can do on this module. Applies instantly to all users with this role.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                {Object.entries(CHIP).map(([key, cfg]) => (
                  <label key={key} className="flex items-center gap-3 cursor-pointer" data-testid={`toggle-${key}`}>
                    <Checkbox
                      checked={editing.draft[key]}
                      onCheckedChange={(v) =>
                        setEditing((p) => ({ ...p, draft: { ...p.draft, [key]: !!v } }))
                      }
                    />
                    <span className={`inline-flex items-center justify-center h-6 w-6 rounded text-xs font-bold border ${editing.draft[key] ? cfg.on : "bg-slate-100 text-slate-300 border-slate-200"}`}>
                      {cfg.letter}
                    </span>
                    <span className="text-sm text-slate-700">{cfg.label}</span>
                  </label>
                ))}
                {editing.partialUpdate && !editing.draft.update && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                    This role currently has a <b>partial field-level edit list</b> ({editing.partialUpdate.join(", ")}) from the code default.
                    Saving with Edit off removes editing entirely; saving with Edit on grants full edit.
                  </div>
                )}
                {editing.source === "override" && (
                  <Button variant="outline" size="sm" onClick={revert} disabled={saving} data-testid="matrix-revert">
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Revert to default
                  </Button>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setEditing(null)} data-testid="matrix-cancel">Cancel</Button>
                  <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="matrix-save">
                    {saving ? "Saving…" : "Save Permission"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
